import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { Subject, takeUntil, distinctUntilChanged } from 'rxjs';
import { DropDown } from 'src/app/interfaces/drop-down.interface';
import { ViewModelCheckboxListComponent } from '../view-model-checkbox-list/view-model-checkbox-list.component';

@Component({
  selector: 'app-multi-select-expansion-field',
  templateUrl: './multi-select-expansion-field.component.html',
  styleUrls: ['./multi-select-expansion-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectExpansionFieldComponent
  implements OnDestroy, OnChanges
{
  @ViewChild('childSelectControl')
  public childSelectControl: ViewModelCheckboxListComponent;

  @Input() expanded: boolean = false;
  @Output() checkboxChanged = new EventEmitter<{ key: string; value: boolean }>();

  master_indeterminate: boolean = false;
  master_checked: boolean = false;

  private destroy$ = new Subject<void>();
  private _formGroup: UntypedFormGroup | FormGroup;
  public get formGroup(): UntypedFormGroup | FormGroup {
    return this._formGroup;
  }
  @Input()
  public set formGroup(value: UntypedFormGroup | FormGroup) {
    this._formGroup = value;

    //if the form group is disabled, disable the header form group
    this._formGroup?.statusChanges
      ?.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      ?.subscribe((status) => {
        if (status === 'DISABLED' && this.headerFormGroup?.enabled) {
          this.headerFormGroup?.disable();
        } else if (this.headerFormGroup?.disabled) {
          this.headerFormGroup?.enable();
        }
      });
  }

  private _fieldControlName: any;
  public get fieldControlName(): any {
    return this._fieldControlName;
  }
  @Input()
  public set fieldControlName(value: any) {
    this._fieldControlName = value;
  }

  @Input() formState: Object = null;
  @Input() options: DropDown<string>[];
  @Input() headerLabel: string = 'headerLabel';

  headerFormGroup = new FormGroup({
    headerToggle: new FormControl(false),
  });

  get selectedCount(): number {
    return Object.keys(
      this.formGroup?.get(this.fieldControlName)?.value ?? {},
    ).filter(
      (key) => this.formGroup.get(this.fieldControlName).value[key] === true,
    ).length;
  }

  constructor(private _cdr: ChangeDetectorRef) {
    //listen to changes in the header toggle. If the header toggle is checked, select all the options
    this.headerFormGroup
      .get('headerToggle')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((checked: boolean) => {
        if (this.options?.length > 0) {
          let val = {};
          this.options.forEach((option) => {
            val[option.value] = checked;
          });
          this.formGroup?.get(this.fieldControlName)?.setValue(val);
        }
        this.formGroup?.markAsTouched();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    //create the form control for the selected values if it does not exist
    if (
      this.formGroup &&
      this.fieldControlName &&
      !this.formGroup.get(this.fieldControlName)
    ) {
      const disabled = this.formGroup.disabled;
      this.formGroup.addControl(this.fieldControlName, new FormControl({}));
      this.formGroup
        .get(this.fieldControlName)
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          //set the header toggle state based on the selected values
          const optLen = this.options?.length ?? 0;
          const valLen = Object.keys(value ?? {}).filter(
            (key) => value[key] === true,
          ).length;
          this.master_indeterminate = valLen > 0 && valLen < optLen;
          this.master_checked = valLen === optLen;
          if (this.master_checked) {
            this.headerFormGroup
              .get('headerToggle')
              .setValue(true, { emitEvent: false });
          }
        });

      //disable the form group if the parent form group is disabled
      if (disabled) {
        this.formGroup.disable();
        this.headerFormGroup.disable();
      }
    }

    if (changes.formState) {
      this.formGroup
        .get(this.fieldControlName)
        .setValue(changes.formState.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectionChange() {
    this.formGroup.markAsTouched();
  }
}
